import { useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import type { AOFormData, Service, Collaborateur, PromptTemplate } from "../AOWizard";

interface AOGeneralStepProps {
  formData: AOFormData;
  setFormData: (data: AOFormData) => void;
  services: Service[];
  collaborateurs: Collaborateur[];
  promptTemplates: PromptTemplate[];
}

export function AOGeneralStep({ formData, setFormData, services, collaborateurs, promptTemplates }: AOGeneralStepProps) {
  const handleInputChange = (field: keyof AOFormData, value: string | number) => {
    setFormData({ ...formData, [field]: value });
  };

  // Debug logging
  useEffect(() => {
    console.log('AOGeneralStep: promptTemplates count:', promptTemplates.length);
    console.log('AOGeneralStep: promptTemplates:', promptTemplates);
  }, [promptTemplates]);

  return (
    <div className="space-y-6">
      {/* Informations de base */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Informations générales</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="titre">Titre *</Label>
            <Input
              id="titre"
              value={formData.titre}
              onChange={(e) => handleInputChange('titre', e.target.value)}
              placeholder="Ex: Développement application mobile"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="reference">Référence *</Label>
            <Input
              id="reference"
              value={formData.reference}
              onChange={(e) => handleInputChange('reference', e.target.value)}
              placeholder="Ex: AO-2024-001"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            placeholder="Description détaillée de l'appel d'offres..."
            rows={3}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="prompt_template_id">Type d'analyse</Label>
          <Select
            value={formData.prompt_template_id || "none"}
            onValueChange={(value) => handleInputChange('prompt_template_id', value === "none" ? "" : value)}
          >
            <SelectTrigger className="bg-background border border-border">
              <SelectValue placeholder="Sélectionner le type d'analyse" />
            </SelectTrigger>
            <SelectContent className="bg-background border border-border z-50 shadow-lg">
              <SelectItem value="none">Sélectionner un type</SelectItem>
              {promptTemplates.map((template) => (
                <SelectItem key={template.id} value={template.id}>
                  {template.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Planning */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Planning</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="date_debut_redaction_ao">Début rédaction (AO, Annonce,..)</Label>
            <Input
              id="date_debut_redaction_ao"
              type="date"
              value={formData.date_debut_redaction_ao}
              onChange={(e) => handleInputChange('date_debut_redaction_ao', e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="date_publication">Date publication</Label>
            <Input
              id="date_publication"
              type="date"
              value={formData.date_publication}
              onChange={(e) => handleInputChange('date_publication', e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="date_limite">Date limite soumission</Label>
            <Input
              id="date_limite"
              type="date"
              value={formData.date_limite}
              onChange={(e) => handleInputChange('date_limite', e.target.value)}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="date_qr_soumissionnaires">Q&R soumissionnaires</Label>
            <Input
              id="date_qr_soumissionnaires"
              type="date"
              value={formData.date_qr_soumissionnaires}
              onChange={(e) => handleInputChange('date_qr_soumissionnaires', e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="date_reponses_qr">Réponses Q&R</Label>
            <Input
              id="date_reponses_qr"
              type="date"
              value={formData.date_reponses_qr}
              onChange={(e) => handleInputChange('date_reponses_qr', e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="date_analyse_offres">Analyse Offres</Label>
            <Input
              id="date_analyse_offres"
              type="date"
              value={formData.date_analyse_offres}
              onChange={(e) => handleInputChange('date_analyse_offres', e.target.value)}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="space-y-2">
            <Label htmlFor="date_debut_soutenance">Début soutenance</Label>
            <Input
              id="date_debut_soutenance"
              type="date"
              value={formData.date_debut_soutenance}
              onChange={(e) => handleInputChange('date_debut_soutenance', e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="date_fin_soutenance">Fin soutenance</Label>
            <Input
              id="date_fin_soutenance"
              type="date"
              value={formData.date_fin_soutenance}
              onChange={(e) => handleInputChange('date_fin_soutenance', e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="date_adjudication">Adjudication</Label>
            <Input
              id="date_adjudication"
              type="date"
              value={formData.date_adjudication}
              onChange={(e) => handleInputChange('date_adjudication', e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="date_contractualisation">Contractualisation</Label>
            <Input
              id="date_contractualisation"
              type="date"
              value={formData.date_contractualisation}
              onChange={(e) => handleInputChange('date_contractualisation', e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Contacts et ressources */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Contacts et responsabilités</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="ao_manager">Responsable - chargé d'affaires</Label>
            <Select
              value={formData.ao_manager || "none"}
              onValueChange={(value) => {
                if (value === "none") {
                  setFormData({
                    ...formData,
                    ao_manager: "",
                  });
                  return;
                }
                const selectedCollaborateur = collaborateurs.find(c => c.nom === value);
                const serviceId = selectedCollaborateur?.service_id || formData.service_responsable_id || "";
                setFormData({
                  ...formData,
                  ao_manager: value,
                  service_responsable_id: serviceId,
                });
              }}
            >
              <SelectTrigger className="bg-background border border-border">
                <SelectValue placeholder="Sélectionner un manager" />
              </SelectTrigger>
              <SelectContent className="bg-background border border-border z-50 shadow-lg">
                <SelectItem value="none">Aucun manager</SelectItem>
                {collaborateurs.map((collaborateur) => (
                  <SelectItem key={collaborateur.id} value={collaborateur.nom}>
                    {collaborateur.nom}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="interlocuteur_da">Interlocuteur DA</Label>
            <Input
              id="interlocuteur_da"
              value={formData.interlocuteur_da}
              onChange={(e) => handleInputChange('interlocuteur_da', e.target.value)}
              placeholder="Contact côté donneur d'ordre"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="service_responsable">Service responsable</Label>
            <Select
              value={formData.service_responsable_id || "none"}
              onValueChange={(value) => handleInputChange('service_responsable_id', value === "none" ? "" : value)}
            >
              <SelectTrigger className="bg-background border border-border">
                <SelectValue placeholder="Sélectionner un service" />
              </SelectTrigger>
              <SelectContent className="bg-background border border-border z-50 shadow-lg">
                <SelectItem value="none">Aucun service</SelectItem>
                {services.map((service) => (
                  <SelectItem key={service.id} value={service.id}>
                    {service.nom}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="lien_ao">Lien de l'AO</Label>
            <Input
              id="lien_ao"
              type="url"
              value={formData.lien_ao}
              onChange={(e) => handleInputChange('lien_ao', e.target.value)}
              placeholder="https://..."
            />
          </div>
        </div>
      </div>
    </div>
  );
}